import { ChevronDownIcon } from "../../Icons";
import { themeAtom } from "../../atoms";
import LoadingIndicator from "../LoadingIndicator";
import { useClickOutside } from "./useClickOutside";
import { useAtomValue } from "jotai";
import React, { ReactNode, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

export type Props = {
  options?: ReadonlyArray<string>;
  placeholder?: string;
  icon?: ReactNode;
  value?: string;
  error?: string;
  onChange?: (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: any;
  onSelectOption?: (option: string) => void;
  floatingComponent?: any;
  isOpen?: boolean;
  highlighted?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
  className?: string;
  ariaLabel?: string;
};

const Dropdown: React.FC<Props> = ({
  placeholder,
  icon,
  value = "",
  onChange,
  onBlur,
  onClick,
  onSelectOption,
  error,
  options = [],
  floatingComponent,
  highlighted,
  disabled = false,
  isLoading,
  readOnly,
  className,
  ariaLabel,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [_value, setValue] = useState(value);
  const [filterTerm, setFilterTerm] = useState("");
  const floatingDivRef = React.useRef<HTMLDivElement>(null);
  const [inHoverState, setInHoverState] = useState(false);
  const theme = useAtomValue(themeAtom);

  useClickOutside(() => setIsDropdownOpen(false), floatingDivRef);

  useEffect(() => {
    setValue(value);
  }, [value]);

  const handleDropdownClick = () => {
    if (disabled) return;

    setIsDropdownOpen((prev) => {
      if (!prev && !floatingComponent) {
        setFilterTerm("");
      }
      return !prev;
    });

    onClick?.();
  };

  const handleDropdownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setFilterTerm(event.target.value);
    onChange?.(event);
  };

  const handleOptionClick = (option: string) => (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setValue(option);
    onChange?.(e);
    onSelectOption?.(option); // Only on selecting via existing options (don't trigger it on input change)
  };

  const filteredOptions = options.filter((option) => option.toLowerCase().includes(filterTerm.toLowerCase()));

  return (
    <div className={twMerge("relative", className)} onClick={handleDropdownClick}>
      {icon && (
        <div
          className="pointer-events-none absolute top-1/2 ml-4 w-4 -translate-y-1/2"
          style={{
            color: error
              ? theme.crtDropdown.error
              : isDropdownOpen || highlighted || inHoverState
                ? theme.crtDropdown.open
                : theme.crtDropdown.closed,
          }}
          data-testid="dropdown-icon"
        >
          {icon}
        </div>
      )}

      <input
        type="text"
        aria-label={ariaLabel}
        value={_value}
        onChange={handleDropdownChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid="dropdown-search"
        onBlur={onBlur}
        onMouseEnter={() => setInHoverState(true)}
        onMouseLeave={() => setInHoverState(false)}
        className={twMerge(
          "w-full min-w-[250px] rounded border p-4 py-[11.5px] pr-[34px] text-[12px] outline-none",
          theme.crtDropInput.greenHover,
          theme.crtDropInput.hoverText,
          theme.crtDropInput.hoverPlaceholder,
          theme.crtDropInput.focusBorder,
          theme.crtDropInput.focusVisibleBorder,
          theme.uwhcAssistant.tableRowEven,
          icon && "pl-10",
          error
            ? twMerge(
                theme.crtDropInput.borderError,
                theme.crtDropInput.textError,
                theme.crtDropInput.placeholderTextError,
              )
            : theme.certVesselSelect.tableBorder,
          highlighted
            ? twMerge(
                theme.crtDropInput.borderGreen,
                theme.crtDropInput.textGreen,
                theme.crtDropInput.placeholderTextGreen,
              )
            : theme.home.tailwind.textDefault,
        )}
        readOnly={readOnly}
      />

      {isLoading ? (
        <LoadingIndicator className="pointer-events-none absolute right-[16px] top-[15px] h-4 w-4 opacity-50" />
      ) : (
        <ChevronDownIcon
          className={twMerge(
            "pointer-events-none absolute right-2 top-[12px]",
            "transition-transform duration-[.5s]",
            isDropdownOpen && "rotate-[-180deg]",
          )}
          style={{
            color: error
              ? theme.crtDropdown.error
              : isDropdownOpen || inHoverState
                ? theme.crtDropdown.open
                : theme.crtDropdown.closed,
          }}
        />
      )}

      {isDropdownOpen && floatingComponent && (
        <div ref={floatingDivRef} className="absolute mt-1">
          {floatingComponent}
        </div>
      )}

      {isDropdownOpen && !floatingComponent && options.length > 0 && (
        <div
          ref={floatingDivRef}
          className={twMerge(
            "absolute z-20 mt-1 max-h-[300px] w-[250px] overflow-y-scroll rounded-[4px] border text-[12px] shadow-[0_8px_16px_#45505229]",
            theme.home.tailwind.backgound,
            theme.certVesselSelect.tableBorder,
            "[&::-webkit-scrollbar]:w-1.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            theme.uwhcAssistant.tableScroll,
            theme.uwhcAssistant.scrolHover,
          )}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className={twMerge("cursor-pointer rounded-[4px] px-4 py-[12px]", theme.certVesselSelect.hoverSelect)}
              onClick={handleOptionClick(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}

      {error && <p className={twMerge("absolute mt-1 text-xs", theme.crtDropdown.errorText)}>{error}</p>}
    </div>
  );
};

export default Dropdown;
